/**
 * Country-aware postal address formatting.
 *
 * The app used to assemble every address the American way — "123 Main St,
 * City, Region, Postcode, Country" — even when Azure had handed it a Dutch
 * one. Two things differ by country and both live here:
 *
 *   1. the street line — house number BEFORE the street name (US, GB, FR …)
 *      or AFTER it (NL, DE, BE, most of continental Europe);
 *   2. the locality block — "City, Region Postcode" (US/CA/AU),
 *      "City, Postcode" (GB/IE), or "Postcode City" with no region at all
 *      (the postcode-first countries).
 *
 * Everything else is a comma-joined single line. The country is appended
 * only when it differs from `homeCountry` (an org in Amsterdam does not need
 * "Netherlands" after every one of its own addresses; it does need "Belgium"
 * after a Brussels one). The layouts follow the OpenCage address-formatting
 * templates for the countries in `SUPPORTED_COUNTRY_CODES`; a country not
 * in either set falls back to the historical order.
 */
import { getCountryName } from "@/lib/countries";

export interface PostalAddressParts {
  /** Raw house number as the geocoder returned it ("77B", "12-14"). */
  streetNumber?: string | null;
  /** Raw street name as the geocoder returned it. */
  streetName?: string | null;
  /**
   * A pre-built street line. Used only when `streetNumber`/`streetName` are
   * both absent (legacy callers that carry a single line).
   */
  addressLine?: string | null;
  locality?: string | null;
  adminDistrict?: string | null;
  postalCode?: string | null;
  countryRegion?: string | null;
  countryRegionISO2?: string | null;
  /**
   * The geocoder's own single-line rendering (Azure `freeformAddress`). When
   * present it wins outright — it is already in the local convention and is
   * what `gis_locations.formatted_address` stores and the invoice prints.
   */
  formattedAddress?: string | null;
}

export interface FormatPostalAddressOptions {
  /** ISO 3166-1 alpha-2. When the address is in this country its name is omitted. */
  homeCountry?: string | null;
  /** Override the "foreign only" rule. */
  includeCountry?: "always" | "foreign" | "never";
  /** BCP 47 tag for the country name when the parts carry none. */
  locale?: string;
}

/** House number precedes the street name. */
const NUMBER_FIRST = new Set([
  "US", "CA", "GB", "IE", "AU", "NZ", "FR", "LU", "HK", "SG", "IN", "ZA",
]);

/** "Postcode City" on one line, no region. */
const POSTCODE_BEFORE_CITY = new Set([
  "NL", "BE", "DE", "AT", "CH", "DK", "SE", "NO", "FI", "IT", "ES", "PT",
  "FR", "LU", "PL", "CZ",
]);

/** "City, Region Postcode". */
const REGION_THEN_POSTCODE = new Set(["US", "CA", "AU"]);

/** "City, Postcode" — no region line. */
const CITY_THEN_POSTCODE = new Set(["GB", "IE", "NZ", "SG", "HK"]);

const norm = (v: string | null | undefined): string => (v ?? "").trim();
const iso2 = (v: string | null | undefined): string => norm(v).toUpperCase().slice(0, 2);

/**
 * The street line in the country's own order.
 *
 * `formatStreetLine("77B", "Kloveniersburgwal", "NL")` → "Kloveniersburgwal 77B"
 * `formatStreetLine("1600", "Pennsylvania Avenue", "US")` → "1600 Pennsylvania Avenue"
 */
export function formatStreetLine(
  streetNumber: string | null | undefined,
  streetName: string | null | undefined,
  countryCode: string | null | undefined,
): string {
  const number = norm(streetNumber);
  const name = norm(streetName);
  if (!name) return number;
  if (!number) return name;
  return NUMBER_FIRST.has(iso2(countryCode)) ? `${number} ${name}` : `${name} ${number}`;
}

/**
 * Split a pre-built street line back into number + name, guessing the order
 * from the country. Only for legacy single-line input; prefer the raw fields.
 */
export function splitStreetLine(
  line: string | null | undefined,
  countryCode: string | null | undefined,
): { streetNumber: string; streetName: string } {
  const text = norm(line);
  if (!text) return { streetNumber: "", streetName: "" };
  const parts = text.split(/\s+/);
  if (parts.length < 2) return { streetNumber: "", streetName: text };

  const looksLikeNumber = (token: string) => /\d/.test(token);
  if (NUMBER_FIRST.has(iso2(countryCode))) {
    return looksLikeNumber(parts[0])
      ? { streetNumber: parts[0], streetName: parts.slice(1).join(" ") }
      : { streetNumber: "", streetName: text };
  }
  const last = parts[parts.length - 1];
  if (looksLikeNumber(last)) {
    return { streetNumber: last, streetName: parts.slice(0, -1).join(" ") };
  }
  // Number-last country but the line is number-first (a value written by
  // the pre-locale code path) — accept it rather than lose the number.
  return looksLikeNumber(parts[0])
    ? { streetNumber: parts[0], streetName: parts.slice(1).join(" ") }
    : { streetNumber: "", streetName: text };
}

/**
 * One-line postal address in the address's own country convention.
 *
 * NL: "Kloveniersburgwal 77B, 1011 KA Amsterdam"
 * US: "1600 Pennsylvania Avenue NW, Washington, DC 20500"
 * GB: "10 Downing Street, London, SW1A 2AA"
 * A foreign address (relative to `homeCountry`) gets ", <Country>" appended.
 */
export function formatPostalAddress(
  parts: PostalAddressParts | null | undefined,
  options: FormatPostalAddressOptions = {},
): string {
  if (!parts) return "";
  const country = iso2(parts.countryRegionISO2);
  const home = iso2(options.homeCountry);
  const includeCountry =
    options.includeCountry === "always" ||
    (options.includeCountry !== "never" && (!country || !home || country !== home));
  const countryLabel = includeCountry
    ? norm(parts.countryRegion) || (country ? getCountryName(country, options.locale) : "")
    : "";

  const preformatted = norm(parts.formattedAddress);
  if (preformatted) {
    return countryLabel && !preformatted.toLowerCase().includes(countryLabel.toLowerCase())
      ? `${preformatted}, ${countryLabel}`
      : preformatted;
  }

  const hasRaw = norm(parts.streetNumber) || norm(parts.streetName);
  const street = hasRaw
    ? formatStreetLine(parts.streetNumber, parts.streetName, country)
    : norm(parts.addressLine);
  const locality = norm(parts.locality);
  const region = norm(parts.adminDistrict);
  const postcode = norm(parts.postalCode);

  const lines: string[] = [];
  if (street) lines.push(street);

  if (POSTCODE_BEFORE_CITY.has(country)) {
    const block = [postcode, locality].filter(Boolean).join(" ");
    if (block) lines.push(block);
  } else if (REGION_THEN_POSTCODE.has(country)) {
    if (locality) lines.push(locality);
    const block = [region, postcode].filter(Boolean).join(" ");
    if (block) lines.push(block);
  } else if (CITY_THEN_POSTCODE.has(country)) {
    if (locality) lines.push(locality);
    if (postcode) lines.push(postcode);
  } else {
    // Unknown country: the historical order.
    if (locality) lines.push(locality);
    if (region) lines.push(region);
    if (postcode) lines.push(postcode);
  }

  if (countryLabel) lines.push(countryLabel);
  return lines.join(", ");
}
